/**
 * MasterPublishPanel (DEV-1267, U3) — the author's release controls on a
 * playbook master. The master edits like any mini-project; this panel turns
 * those edits into a published version: it shows the current version, lets
 * the author publish a new one (snapshotting the master's live structure
 * server-side), and lists the version history.
 *
 * Author-only (rendered when the viewer is the master's Lead). Publishing
 * never touches existing projects — new instantiations pick up the new
 * version (Skip, 2026-06-09: future projects only, full stop).
 */

import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  fetchMiniProjectPlaybookByMaster,
  fetchMiniProjectPlaybookVersions,
  publishMiniProjectPlaybook,
  updateMiniProjectPlaybook,
  hardDeleteMiniProjectPlaybook,
  type MiniProjectPlaybook,
  type MiniProjectPlaybookVersion,
} from "../../api/miniProjects";
import { ApiError } from "../../api/client";
import styles from "./MasterPublishPanel.module.css";

interface Props {
  masterMiniProjectGuid: string;
}

export default function MasterPublishPanel({ masterMiniProjectGuid }: Props) {
  const { t } = useAppTranslation("pages");
  const navigate = useNavigate();
  const [playbook, setPlaybook] = useState<MiniProjectPlaybook | null>(null);
  const [versions, setVersions] = useState<MiniProjectPlaybookVersion[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [composing, setComposing] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justPublished, setJustPublished] = useState<number | null>(null);
  const [confirmingHide, setConfirmingHide] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleteTyped, setDeleteTyped] = useState("");

  const load = useCallback(async () => {
    try {
      const pb = await fetchMiniProjectPlaybookByMaster(masterMiniProjectGuid);
      setPlaybook(pb);
      const vs = await fetchMiniProjectPlaybookVersions(pb.playbookGuid);
      setVersions(vs);
    } catch {
      // No playbook resolves for this master (shouldn't happen for a real
      // master) — render nothing.
      setPlaybook(null);
    }
  }, [masterMiniProjectGuid]);

  useEffect(() => {
    load();
  }, [load]);

  const doPublish = useCallback(async () => {
    if (!playbook) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await publishMiniProjectPlaybook(playbook.playbookGuid, {
        publishedContent: "{}", // ignored — master-backed publishes from the master
        changeNotes: notes.trim() || undefined,
      });
      setPlaybook(updated);
      setJustPublished(updated.currentVersion);
      setComposing(false);
      setNotes("");
      const vs = await fetchMiniProjectPlaybookVersions(playbook.playbookGuid);
      setVersions(vs);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, [playbook, notes]);

  // DEV-1277 — catalog visibility. Reversible soft-hide: isActive=false drops
  // the playbook from the catalog (no new instantiations) while leaving the
  // master, version history, and every already-created instance untouched.
  const setActive = useCallback(
    async (active: boolean) => {
      if (!playbook) return;
      setBusy(true);
      setError(null);
      try {
        const updated = await updateMiniProjectPlaybook(playbook.playbookGuid, { isActive: active });
        setPlaybook(updated);
        setConfirmingHide(false);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    },
    [playbook],
  );

  // DEV-1277 — permanent delete lives HERE (on the master), not on the
  // catalog/management list, so it's a deliberate destination rather than a
  // fat-finger risk in a row of buttons. Irreversible; type-to-confirm.
  const doDelete = useCallback(async () => {
    if (!playbook) return;
    setBusy(true);
    setError(null);
    try {
      await hardDeleteMiniProjectPlaybook(playbook.playbookGuid);
      // The master is now deleted — leave this (defunct) page for the owner.
      navigate(`/communities/${playbook.ownerCommunityGuid}`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        setError((e.body as any)?.message || t("playbooksManage.deleteBlocked"));
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
      setBusy(false);
    }
  }, [playbook, navigate, t]);

  if (!playbook) return null;

  return (
    <div className={styles.panel}>
      <div className={styles.row}>
        <span className={styles.badge}>
          {t("masterPublish.versionBadge", { count: playbook.currentVersion })}
        </span>
        <span className={styles.spacer} />
        {!composing && (
          <>
            <button className={styles.linkBtn} onClick={() => setHistoryOpen((h) => !h)}>
              {t("masterPublish.history")}
            </button>
            <button className={styles.publishBtn} onClick={() => { setComposing(true); setJustPublished(null); }}>
              {t("masterPublish.publishBtn")}
            </button>
          </>
        )}
      </div>

      {justPublished != null && !composing && (
        <div className={styles.okNote}>
          {t("masterPublish.publishedOk", { count: justPublished })}
        </div>
      )}

      {composing && (
        <div className={styles.composer}>
          <div className={styles.intro}>{t("masterPublish.intro")}</div>
          <label className={styles.fl}>{t("masterPublish.notesLabel")}</label>
          <textarea
            className={styles.textarea}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("masterPublish.notesPlaceholder")}
            rows={3}
            autoFocus
          />
          {error && <div className={styles.error}>{error}</div>}
          <div className={styles.composerActions}>
            <button className={styles.ghostBtn} onClick={() => { setComposing(false); setError(null); }} disabled={busy}>
              {t("masterPublish.cancel")}
            </button>
            <button className={styles.publishBtn} onClick={doPublish} disabled={busy}>
              {busy ? t("masterPublish.publishing") : t("masterPublish.confirm")}
            </button>
          </div>
        </div>
      )}

      {historyOpen && (
        <div className={styles.history}>
          {versions.length === 0 ? (
            <div className={styles.muted}>{t("masterPublish.noVersions")}</div>
          ) : (
            versions
              .slice()
              .sort((a, b) => b.version - a.version)
              .map((v) => (
                <div key={v.version} className={styles.versionRow}>
                  <span className={styles.vNum}>v{v.version}</span>
                  <span className={styles.vDate}>
                    {new Date(v.publishedAt).toLocaleDateString()}
                  </span>
                  <span className={styles.vNotes}>{v.changeNotes || "—"}</span>
                </div>
              ))
          )}
        </div>
      )}

      {/* Catalog visibility (DEV-1277). Hidden while composing a publish. */}
      {!composing && (
        playbook.isActive ? (
          confirmingHide ? (
            <div className={styles.composer}>
              <div className={styles.intro}>{t("masterPublish.hidePrompt")}</div>
              {error && <div className={styles.error}>{error}</div>}
              <div className={styles.composerActions}>
                <button className={styles.ghostBtn} onClick={() => { setConfirmingHide(false); setError(null); }} disabled={busy}>
                  {t("masterPublish.cancel")}
                </button>
                <button className={styles.dangerBtn} onClick={() => setActive(false)} disabled={busy}>
                  {busy ? t("masterPublish.hiding") : t("masterPublish.hideConfirm")}
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.visRow}>
              <span className={styles.visState}>{t("masterPublish.catalogListed")}</span>
              <span className={styles.spacer} />
              <button className={styles.linkBtn} onClick={() => setConfirmingHide(true)}>
                {t("masterPublish.hideBtn")}
              </button>
            </div>
          )
        ) : (
          <div className={styles.hiddenBanner}>
            <span className={styles.hiddenText}>{t("masterPublish.hiddenBanner")}</span>
            {error && <div className={styles.error}>{error}</div>}
            <button className={styles.publishBtn} onClick={() => setActive(true)} disabled={busy}>
              {busy ? t("masterPublish.restoring") : t("masterPublish.restoreBtn")}
            </button>
          </div>
        )
      )}

      {/* Danger zone — permanent delete (DEV-1277). Lives on the master, not
          the catalog/management list, so it's a deliberate destination.
          Hidden while composing a publish or confirming a hide. */}
      {!composing && !confirmingHide && (
        <div className={styles.dangerZone}>
          {confirmingDelete ? (
            <>
              <div className={styles.intro}>{t("playbooksManage.deletePrompt", { title: playbook.title })}</div>
              <input
                className={styles.delInput}
                value={deleteTyped}
                onChange={(e) => setDeleteTyped(e.target.value)}
                placeholder={playbook.title}
                aria-label={t("playbooksManage.deletePermanently")}
                autoFocus
              />
              {error && <div className={styles.error}>{error}</div>}
              <div className={styles.composerActions}>
                <button
                  className={styles.ghostBtn}
                  onClick={() => { setConfirmingDelete(false); setDeleteTyped(""); setError(null); }}
                  disabled={busy}
                >
                  {t("masterPublish.cancel")}
                </button>
                <button
                  className={styles.dangerBtn}
                  onClick={doDelete}
                  disabled={busy || deleteTyped.trim() !== playbook.title}
                >
                  {busy ? t("playbooksManage.deleting") : t("playbooksManage.deleteConfirm")}
                </button>
              </div>
            </>
          ) : (
            <button
              className={styles.deleteLink}
              onClick={() => { setConfirmingDelete(true); setDeleteTyped(""); setError(null); }}
            >
              {t("playbooksManage.deletePermanently")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
